/**
 * Batch processor utility that processes an array in chunks and returns merged results
 *
 * @param items - Array of items to process in batches
 * @param per_page - Number of items per batch
 * @param callback - Function that receives a chunk of items and returns array of results (or void for side-effects)
 * @returns Array of all results merged together (or void if callback returns void)
 *
 * @example
 * // With results
 * const results = await processBatches({
 *   items: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
 *   per_page: 3,
 *   callback: async (batch) => {
 *     return batch.map(n => n * 2);
 *   }
 * });
 * // results will be: [2, 4, 6, 8, 10, 12, 14, 16, 18, 20]
 *
 * @example
 * // Without results (side-effects only)
 * await processBatches({
 *   items: [1, 2, 3, 4, 5],
 *   per_page: 2,
 *   callback: async (batch) => {
 *     await db.insert(batch);
 *     // No return statement
 *   }
 * });
 */
export async function processBatches<T, R>({
	items,
	per_page,
	callback,
}: {
	items: T[];
	per_page: number;
	callback: (batch: T[]) => Promise<R[]>;
}): Promise<R[]>;

export async function processBatches<T>({
	items,
	per_page,
	callback,
}: {
	items: T[];
	per_page: number;
	callback: (batch: T[]) => Promise<void>;
}): Promise<void>;

export async function processBatches<T, R>({
	items,
	per_page,
	callback,
}: {
	items: T[];
	per_page: number;
	callback: (batch: T[]) => Promise<R[] | void>;
}): Promise<R[] | void> {
	const results: R[] = [];
	let returns_void = false;

	for (let i = 0; i < items.length; i += per_page) {
		const batch = items.slice(i, i + per_page);
		const batch_results = await callback(batch);

		if (batch_results === undefined) {
			returns_void = true;
		} else {
			results.push(...batch_results);
		}
	}

	return returns_void ? undefined : results;
}
